import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageX, PackagePlus, Pencil } from "lucide-react";
import { useApp } from "@/lib/app-context";
import { errMsg } from "@/lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { StockQuickEdit } from "./StockQuickEdit";

const seenKey = (b) => `zilabi-oos-seen-${b}`;
const readSeen = (b) => { try { const v = JSON.parse(localStorage.getItem(seenKey(b)) || "{}"); return Array.isArray(v) ? {} : v; } catch { return {}; } };
const isSeen = (seen, r) => seen[r.product_id] && (!r.last_stock_update || r.last_stock_update <= seen[r.product_id]);

const AlertItem = ({ r, canEdit, busy, onRestock, onEdit }) => (
  <div className="flex min-w-0 items-center gap-2 sm:gap-3 rounded-lg border border-red-100 bg-red-50/60 p-2.5" data-testid={`restock-alert-item-${r.product_id}`}>
    {r.image ? <img src={r.image} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="h-10 w-10 rounded-md object-cover border bg-white" /> : <div className="h-10 w-10 rounded-md bg-white border" />}
    <div className="min-w-0 flex-1 text-sm">
      <div className="font-semibold truncate">{r.name}</div>
      <div className="text-xs text-muted-foreground">{r.erp_code} · {r.category || "Uncategorised"} · Rack {r.racks.join(", ") || "-"}</div>
    </div>
    {canEdit && <>
      <Button size="sm" disabled={busy} onClick={() => onRestock(r)} data-testid={`restock-one-tap-${r.product_id}`}><PackagePlus className="h-4 w-4 mr-1" />+{r.suggested_reorder}</Button>
      <Button size="icon" variant="ghost" onClick={() => onEdit(r.product_id)} data-testid={`restock-edit-${r.product_id}`}><Pencil className="h-4 w-4" /></Button>
    </>}
  </div>
);

export function RestockAlerts({ onChanged }) {
  const { api, branchId, can } = useApp();
  const [fresh, setFresh] = useState([]);
  const [busy, setBusy] = useState(null);
  const [edit, setEdit] = useState(null);
  const check = async () => {
    const rows = (await api.get("/stock/status-list", { params: { kind: "out", branch_id: branchId } })).data.filter((r) => r.active);
    const all = readSeen(branchId);
    const seen = Object.fromEntries(rows.filter((r) => isSeen(all, r)).map((r) => [r.product_id, all[r.product_id]]));
    localStorage.setItem(seenKey(branchId), JSON.stringify(seen));
    setFresh(rows.filter((r) => !isSeen(seen, r)));
  };
  useEffect(() => {
    check().catch(() => {});
    const t = setInterval(() => check().catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const markSeen = (ids) => { const now = new Date().toISOString(); localStorage.setItem(seenKey(branchId), JSON.stringify({ ...readSeen(branchId), ...Object.fromEntries(ids.map((id) => [id, now])) })); };
  const dismiss = () => { markSeen(fresh.map((r) => r.product_id)); setFresh([]); };
  const restock = async (r) => {
    setBusy(r.product_id);
    try {
      await api.post("/stock/set-available", { product_id: r.product_id, branch_id: branchId, new_available: r.suggested_reorder, reason: "Restocked from dashboard out-of-stock alert" });
      toast.success(`${r.name}: available 0 → ${r.suggested_reorder}. Website sync queued.`);
      setFresh((f) => f.filter((x) => x.product_id !== r.product_id));
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  return <>
    <Dialog open={fresh.length > 0 && !edit} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-lg max-h-[85vh] overflow-y-auto grid-cols-[minmax(0,1fr)] rounded-xl" data-testid="restock-alert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><PackageX className="h-5 w-5" />{fresh.length} product{fresh.length > 1 ? "s" : ""} just ran out</DialogTitle>
          <DialogDescription>{can("inventory.edit") ? "Tap + to restock the suggested quantity, or the pencil to set an exact amount." : "Ask a manager to restock these products."}</DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-2">{fresh.map((r) => <AlertItem key={r.product_id} r={r} canEdit={can("inventory.edit")} busy={busy === r.product_id} onRestock={restock} onEdit={setEdit} />)}</div>
        <div className="flex justify-end"><Button variant="outline" onClick={dismiss} data-testid="restock-alert-dismiss">Remind me later</Button></div>
      </DialogContent>
    </Dialog>
    <StockQuickEdit productId={edit} onClose={() => { setEdit(null); check().catch(() => {}); }} onChanged={onChanged} />
  </>;
}
